// Palette engine: buildPalette, getCampaignPalettes(Shifted), buildPaletteTheme, hpColor.
import { hexToRgb, rgbToHsl, hslToRgb, rgbToHex, rgbToCss, shiftHueHex } from './colorMath';

export const buildPalette = (sourceHex, theme) => {
  const rgb = hexToRgb(sourceHex);
  if (!rgb) {
    // Defensive fallback — use accent
    const acc = theme.colors.accent;
    const accRgb = hexToRgb(acc) || { r: 200, g: 200, b: 200 };
    return {
      color: acc,
      light: theme.colors.accentLight || acc,
      bg: rgbToCss(accRgb, 0.18),
      bgStrong: rgbToCss(accRgb, 0.28),
      border: acc,
      glow: rgbToCss(accRgb, 0.45),
    };
  }
  const hsl = rgbToHsl(rgb);
  const light = rgbToHex(hslToRgb({ h: hsl.h, s: Math.min(hsl.s + 8, 95), l: Math.min(hsl.l + 22, 82) }));
  return {
    color: sourceHex,
    light,
    bg: rgbToCss(rgb, 0.16),
    bgStrong: rgbToCss(rgb, 0.28),
    border: sourceHex,
    glow: rgbToCss(rgb, 0.45),
  };
};

export const CARD_SURFACE_SOURCE_OVERRIDES = {
  // For Royal Court the identity is regal navy; gold is the jewel accent.
  'royal-court': 'bgCardSolid',
  // For Enchanted Forest the identity is teal-forest; gold is the accent jewel.
  'enchanted-forest': 'bgCardSolid',
  // For Sunset Dusk the identity is the dusk magenta/purple surface; the
  // sunset orange accent renders as muddy brown when used for card bodies.
  'sunset-dusk': 'bgCardSolid',
};

export const getCampaignPalettes = (theme) => {
  const c = theme.colors;
  const accentRgb = hexToRgb(c.accent);
  if (!accentRgb) {
    return [
      buildPalette(c.accent, theme),
      buildPalette(c.accent, theme),
      buildPalette(c.accent, theme),
      buildPalette(c.accent, theme),
      buildPalette(c.accent, theme),
    ];
  }
  const accentHsl = rgbToHsl(accentRgb);

  // dh: hue rotation, ds: saturation delta, dl: lightness delta
  const slot = (dh, ds, dl) => rgbToHex(hslToRgb({
    h: accentHsl.h + dh,
    s: Math.max(30, Math.min(85, accentHsl.s + ds)),
    l: Math.max(40, Math.min(70, accentHsl.l + dl)),
  }));

  // Determine where the *card body* hue should come from. For most themes
  // this is the accent itself. For jeweled themes, it's the surface color.
  const surfaceSourceKey = CARD_SURFACE_SOURCE_OVERRIDES[theme.id];
  const surfaceSourceHex = surfaceSourceKey ? c[surfaceSourceKey] : c.accent;
  const surfaceSourceRgb = hexToRgb(surfaceSourceHex) || accentRgb;
  const surfaceSourceHsl = rgbToHsl(surfaceSourceRgb);

  // For surface variation we rotate around the surface source hue, not the
  // accent hue. Each palette object keeps `color`/`light`/etc. tied to the
  // accent rotation (used for borders, stripes, icons), and adds a separate
  // `surfaceHex` for the card body to consume.
  const surfaceSlot = (dh, ds, dl) => rgbToHex(hslToRgb({
    h: surfaceSourceHsl.h + dh,
    s: Math.max(20, Math.min(70, surfaceSourceHsl.s + ds)),
    l: Math.max(20, Math.min(60, surfaceSourceHsl.l + dl)),
  }));

  const slots = [
    { dh: 0,   ds: 0,   dl: 0 },   // 0: identity
    { dh: -15, ds: +5,  dl: -8 },  // 1: cooler/deeper
    { dh: +18, ds: 0,   dl: +5 },  // 2: warmer/brighter
    { dh: -22, ds: -8,  dl: +6 },  // 3: cool, paler
    { dh: +22, ds: +5,  dl: -3 },  // 4: warm
  ];

  return slots.map((s, idx) => {
    const accentForSlot = idx === 0 ? c.accent : slot(s.dh, s.ds, s.dl);
    const palette = buildPalette(accentForSlot, theme);
    // Attach the surface source hex so the card-surface generator can use it.
    palette.surfaceHex = idx === 0 ? surfaceSourceHex : surfaceSlot(s.dh, s.ds, s.dl);
    return palette;
  });
};

export const getCampaignPalettesShifted = (theme, hueShift) => {
  if (!hueShift) return getCampaignPalettes(theme);
  // Synthesize a theme variant whose accent (and its derived light/dark/glow)
  // sit at a different point on the color wheel. The palette generator will
  // then rotate its 5 slots around *that* point.
  const c = theme.colors;
  const shiftedTheme = {
    ...theme,
    colors: {
      ...c,
      accent: shiftHueHex(c.accent, hueShift),
      accentLight: shiftHueHex(c.accentLight || c.accent, hueShift),
      accentDark: shiftHueHex(c.accentDark || c.accent, hueShift),
      // Note: bgCardSolid stays unshifted. That's the theme's identity —
      // we want the surface lightness/depth of the original theme; only the
      // accent overlay rotates.
    },
  };
  return getCampaignPalettes(shiftedTheme);
};

export const hpColor = (cur, max, theme) => {
  const pct = cur / max;
  if (pct > 0.6) return theme.colors.hpHigh;
  if (pct > 0.3) return theme.colors.hpMid;
  return theme.colors.hpLow;
};

export const buildPaletteTheme = (baseTheme, palette, cardStyle, cardFinish, options = {}) => {
  const { soloDarken = 0, alphaProfile = 'campaign' } = options;
  const c = baseTheme.colors;
  const e = baseTheme.effects || {};

  const surfaceSourceRgb = hexToRgb(palette.surfaceHex || palette.color) || { r: 120, g: 120, b: 120 };
  const surfaceSourceHsl = rgbToHsl(surfaceSourceRgb);
  const sourceRgb = hexToRgb(palette.color) || surfaceSourceRgb;
  const baseCardRgb = hexToRgb(c.bgCardSolid) || { r: 28, g: 16, b: 40 };
  const baseCardHsl = rgbToHsl(baseCardRgb);

  const isVividHueRange = surfaceSourceHsl.h >= 60 && surfaceSourceHsl.h <= 180;
  const isPureGreenRange = surfaceSourceHsl.h >= 90 && surfaceSourceHsl.h <= 170;
  const greenDarken = isPureGreenRange ? 4 : 0;
  // Campaign cards floor at L=15 to lift Dark Arcana (#1c1028, L=11) out of
  // grey; solo cards floor at L=11 to stay quieter via the soloDarken offset.
  const lightnessFloor = soloDarken > 0 ? 11 : 15;
  const surfaceL = Math.max(lightnessFloor, Math.min(28, baseCardHsl.l + 3 - greenDarken - soloDarken));
  const surfaceLHover = Math.max(soloDarken > 0 ? 9 : 10, Math.min(soloDarken > 0 ? 34 : 38, surfaceL + (soloDarken > 0 ? 5 : 9)));
  const surfaceLTop = surfaceL + 3;
  const surfaceLTopHover = surfaceLHover + 4;
  const satMultiplier = isVividHueRange ? 0.32 : 0.55;
  const satMax = isVividHueRange ? 22 : 28;
  const satMin = isVividHueRange ? 18 : 26;
  const surfaceSat = Math.max(satMin, Math.min(satMax, surfaceSourceHsl.s * satMultiplier));
  const surfaceSatHover = soloDarken > 0
    ? surfaceSat + 4
    : Math.min(isVividHueRange ? 32 : 38, surfaceSat + (isVividHueRange ? 6 : 9));

  const baseSurfaceRgb = hslToRgb({ h: surfaceSourceHsl.h, s: surfaceSat, l: surfaceL });
  const hoverSurfaceRgb = hslToRgb({ h: surfaceSourceHsl.h, s: surfaceSatHover, l: surfaceLHover });
  const topGradientRgb = hslToRgb({ h: surfaceSourceHsl.h, s: surfaceSat + 4, l: surfaceLTop });
  const topGradientHoverRgb = hslToRgb({ h: surfaceSourceHsl.h, s: surfaceSatHover + 4, l: surfaceLTopHover });

  const baseSurfaceHex = rgbToHex(baseSurfaceRgb);
  const hoverSurfaceHex = rgbToHex(hoverSurfaceRgb);
  const topGradientHex = rgbToHex(topGradientRgb);
  const topGradientHoverHex = rgbToHex(topGradientHoverRgb);

  // Alpha schedules differ between campaign and solo profiles. Solo is
  // ghostier across the board.
  let translucentAlpha = 1;
  let translucentAlphaHover = 1;
  if (alphaProfile === 'solo') {
    if (cardStyle === 'glass') { translucentAlpha = 0.16; translucentAlphaHover = 0.30; }
    else if (cardStyle === 'elevated' || cardStyle === 'bordered') { translucentAlpha = 0.28; translucentAlphaHover = 0.45; }
    else { translucentAlpha = 0.85; translucentAlphaHover = 0.92; }
  } else {
    if (cardStyle === 'glass') { translucentAlpha = 0.52; translucentAlphaHover = 0.68; }
    else if (cardStyle === 'elevated') { translucentAlpha = 0.78; translucentAlphaHover = 0.88; }
    else if (cardStyle === 'bordered') { translucentAlpha = 0.55; translucentAlphaHover = 0.75; }
  }
  const useTranslucent = alphaProfile === 'solo' || cardStyle === 'glass' || cardStyle === 'elevated' || cardStyle === 'bordered';

  // Surface values for use in finishes (tinted gradient or solid).
  const baseSurfaceForFinish = useTranslucent ? rgbToCss(baseSurfaceRgb, translucentAlpha) : baseSurfaceHex;
  const hoverSurfaceForFinish = useTranslucent ? rgbToCss(hoverSurfaceRgb, translucentAlphaHover) : hoverSurfaceHex;
  const topGradientForFinish = useTranslucent ? rgbToCss(topGradientRgb, translucentAlpha) : topGradientHex;
  const topGradientHoverForFinish = useTranslucent ? rgbToCss(topGradientHoverRgb, translucentAlphaHover) : topGradientHoverHex;

  let bgCard, bgCardHover, cardShadow, cardShadowHover;
  if (cardFinish === 'solid') {
    bgCard = baseSurfaceForFinish;
    bgCardHover = hoverSurfaceForFinish;
    cardShadow = `0 4px 16px rgba(0,0,0,0.5)`;
    cardShadowHover = `0 8px 24px rgba(0,0,0,0.6)`;
  } else {
    bgCard = `linear-gradient(180deg, ${topGradientForFinish} 0%, ${baseSurfaceForFinish} 100%)`;
    bgCardHover = `linear-gradient(180deg, ${topGradientHoverForFinish} 0%, ${hoverSurfaceForFinish} 100%)`;
    cardShadow = `0 4px 16px rgba(0,0,0,0.45), inset 0 1px 0 ${rgbToCss(sourceRgb, 0.18)}, 0 0 0 1px ${rgbToCss(sourceRgb, 0.22)}`;
    cardShadowHover = `0 10px 28px rgba(0,0,0,0.55), inset 0 1px 0 ${rgbToCss(sourceRgb, 0.26)}, 0 0 22px ${palette.glow}`;
  }

  const glassGlow = (cardStyle === 'glass' || cardStyle === 'elevated')
    ? `rgba(${sourceRgb.r}, ${sourceRgb.g}, ${sourceRgb.b}, 0.7)`
    : palette.glow;

  return {
    ...baseTheme,
    colors: {
      ...c,
      accent: palette.color,
      accentLight: palette.light,
      accentGlow: glassGlow,
      borderPrimary: palette.border,
      borderAccent: palette.border,
      bgCard: useTranslucent && cardFinish === 'solid' ? baseSurfaceForFinish : bgCard,
      bgCardSolid: baseSurfaceForFinish,
      bgCardHover: hoverSurfaceForFinish,
    },
    effects: {
      ...e,
      cardShadow,
      cardShadowHover,
    },
  };
};
